import React, { useState, useEffect } from 'react';
import { getEmailSignature, saveEmailSignature } from '../api/emails';
import './SignatureSettings.css';

interface SignatureSettingsProps {
  onClose?: () => void;
}

export default function SignatureSettings({ onClose }: SignatureSettingsProps) {
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    loadSignature();
  }, []);

  const loadSignature = async () => {
    try {
      setLoading(true);
      const data = await getEmailSignature();
      setSignature(data.customSignature || '');
    } catch (error) {
      console.error('Failed to load signature:', error);
      setMessage({ type: 'error', text: 'Failed to load signature' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      await saveEmailSignature(signature, true);
      setMessage({ type: 'success', text: 'Signature saved successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save signature:', error);
      setMessage({ type: 'error', text: 'Failed to save signature' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="signature-settings loading">Loading signature settings...</div>;
  }

  return (
    <div className="signature-settings">
      <div className="signature-header">
        <h2>Email Signature Settings</h2>
        {onClose && (
          <button className="close-button" onClick={onClose}>×</button>
        )}
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="signature-content">
        <div className="info-message">
          <p><strong>Instructions:</strong> Your signature template is pre-loaded below. Simply replace the placeholder text (NAME, TITLE, PHONENUMBER, EMAILADDRESS) with your actual information.</p>
        </div>

        <div className="custom-signature-section">
          <h3>Your Email Signature</h3>
          
          <div className="placeholder-info">
            <p>Replace the following placeholders with your information:</p>
            <ul>
              <li><strong>NAME</strong> → Your full name</li>
              <li><strong>TITLE</strong> → Your job title</li>
              <li><strong>PHONENUMBER</strong> → Your phone number</li>
              <li><strong>EMAILADDRESS</strong> → Your email address</li>
            </ul>
          </div>

          <textarea
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="Your signature will appear here..."
            rows={20}
            className="signature-editor"
          />

          <div className="signature-preview-section">
            <h4>Preview:</h4>
            <div 
              className="signature-preview" 
              dangerouslySetInnerHTML={{ __html: signature }} 
            />
          </div>
        </div>
      </div>

      <div className="signature-footer">
        <button
          onClick={handleSave}
          disabled={saving}
          className="save-button"
        >
          {saving ? 'Saving...' : 'Save Signature'}
        </button>
        {onClose && (
          <button
            onClick={onClose}
            className="cancel-button"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}